import { Component, Host, h, State, Element, Prop, Method, Event, EventEmitter } from '@stencil/core';
import { getHeaderHeight } from '../../../utils/getHeaderHeight';

@Component({
    tag: 'sew-ws-tabs',
    styleUrl: 'sew-ws-tabs.scss',
    shadow: false,
})
export class SewWsTabs {
    @Element() host: HTMLSewWsTabsElement;

    @Prop({ reflect: true }) noGrid = false;
    @Prop({ reflect: true }) noMargin = false;
    @Prop({ reflect: true }) noContent = false;
    @Prop() headline: string;
    @Prop() scrollLeftAriaLabel = 'scroll to left';
    @Prop() scrollRightAriaLabel = 'scroll to right';
    @Prop({ reflect: true }) stickyHeader = false;
    @Prop() noVerticalHeightChange = false;

    @State() activeTab = 0;
    @State() isScrollableToLeft = false;
    @State() isScrollableToRight = false;
    @State() hostChildren: HTMLSewWsTabsItemElement[];
    @State() anchorLink: string;

    @Event() tabChange: EventEmitter<number>;

    @Method()
    async refresh(): Promise<void> {
        this.activeItem();
        this.checkScrollable();
    }

    private tabNavRef: HTMLElement;
    private scrollAreaElementRef: HTMLElement;
    private tabContentRef: HTMLElement;
    private buttonHref: string;
    private tabIndicatorRef: HTMLElement;

    private activeItem = (): void => {
        const tabItems = Array.from(this.tabContentRef.querySelectorAll('sew-ws-tabs-item'));

        // Calculate the maximum height if noVerticalHeightChange is enabled
        let maxHeight = 0;
        if (this.noVerticalHeightChange) {
            maxHeight = Math.max(...tabItems.map((item: HTMLSewWsTabsItemElement) => item.scrollHeight));
        }

        tabItems.map((item: HTMLSewWsTabsItemElement, index) => {
            if (this.isActiveTab(item, index)) {
                item.active = true;
                this.tabContentRef.classList.add('animates');

                // Use maxHeight if noVerticalHeightChange is set, otherwise use item's scrollHeight
                const targetHeight = this.noVerticalHeightChange ? maxHeight : item.scrollHeight;
                this.tabContentRef.style.height = targetHeight + 'px';

                setTimeout(() => {
                    this.tabContentRef.classList.remove('animates');
                }, 500);
            } else {
                item.active = false;
            }
        });

        this.changeButtonHref();
        // Update tab indicator after DOM changes
        requestAnimationFrame(() => this.updateTabIndicator());
    };

    private isActiveTab = (tab: HTMLSewWsTabsItemElement, index): boolean => {
        return (
            (this.activeTab === index && !this.anchorLink) ||
            (this.anchorLink && tab.tabId === this.anchorLink)
        );
    };

    private activeTabHandler(index: number, event: Event): void {
        const rectTabNav = this.tabNavRef.getBoundingClientRect();
        const rectTabItem = (event.target as HTMLElement).getBoundingClientRect();

        if (rectTabNav.right < Math.round(rectTabItem.right)) {
            this.tabNavRef.scrollLeft += rectTabItem.width;
        } else if (rectTabNav.left > rectTabItem.left) {
            this.tabNavRef.scrollLeft -= rectTabItem.width;
        }

        this.activeTab = index;
        // reset anchor link for manual tab change
        this.anchorLink = null;
        this.activeItem();

        // Emit tabChange event with the new active tab index
        this.tabChange.emit(index);
    }

    private saveButtonHref = (): void => {
        const linkElement = this.host.querySelector('sew-ws-button:first-of-type a');

        if (linkElement) {
            this.buttonHref = linkElement.getAttribute('href');
        }
    };

    private changeButtonHref = (): void => {
        const itemButtonHref = this.host.querySelector(
            'sew-ws-tabs-item:nth-child(' + (this.activeTab + 1) + ')'
        );
        const buttonHref = itemButtonHref.getAttribute('button-href');
        const linkElement = this.host.querySelector('sew-ws-button:first-of-type a');

        if (buttonHref && linkElement) {
            linkElement.setAttribute('href', this.buttonHref + buttonHref);
        }
    };

    private checkScrollable = () => {
        const maxScrollLeft = this.tabNavRef.scrollWidth - this.tabNavRef.clientWidth;

        if (this.tabNavRef.scrollLeft === 0) {
            this.isScrollableToLeft = false;
        } else {
            this.isScrollableToLeft = true;
        }

        if (this.tabNavRef.scrollLeft >= maxScrollLeft) {
            this.isScrollableToRight = false;
        } else {
            this.isScrollableToRight = true;
        }
    };

    private scrollToRight = () => {
        const rightEdgeOfScrollArea = this.scrollAreaElementRef.getBoundingClientRect().right;
        const tabsArray = this.scrollAreaElementRef.querySelectorAll('.tab');

        for (let i = 0; i < tabsArray.length; i++) {
            const tab = tabsArray[i] as HTMLElement;
            const tabRect = tab.getBoundingClientRect();
            const isNotVisible = tabRect.right - 1 > rightEdgeOfScrollArea;

            if (isNotVisible) {
                const offset = tabRect.right - rightEdgeOfScrollArea;
                this.tabNavRef.scrollTo({ left: this.tabNavRef.scrollLeft + offset, behavior: 'smooth' });
                break;
            }
        }
    };

    private scrollToLeft = () => {
        const leftEdgeOfScrollArea = this.scrollAreaElementRef.getBoundingClientRect().left;
        const tabsArray = this.scrollAreaElementRef.querySelectorAll('.tab');

        for (let i = tabsArray.length - 1; i >= 0; i--) {
            const tab = tabsArray[i] as HTMLElement;
            const tabRect = tab.getBoundingClientRect();
            const isNotVisible = tabRect.left + 1 < leftEdgeOfScrollArea;

            if (isNotVisible) {
                // Calculate the scroll offset
                const offset = tab.offsetLeft - this.tabNavRef.scrollLeft;
                this.tabNavRef.scrollTo({ left: this.tabNavRef.scrollLeft + offset, behavior: 'smooth' });
                break;
            }
        }
    };

    componentDidUpdate(): void {
        this.checkScrollable();
    }

    componentDidLoad(): void {
        this.getHostChildren();
        this.saveButtonHref();
        this.getAnchorLink();
        this.activeItem();
        this.checkScrollable();
        this.updateTabIndicator();
        window.addEventListener('hashchange', () => this.getAnchorLink(), false);
    }

    private getHostChildren = (): void => {
        this.hostChildren = [
            ...Array.from(this.tabContentRef.querySelectorAll('sew-ws-tabs-item')),
        ] as HTMLSewWsTabsItemElement[];
    };

    private getAnchorLink = (): void => {
        const url = window.location.href;
        const hashtag = url.split('#')[1];

        // check if anchorLink is in hostChildren
        const anchorLinkExists = this.hostChildren.some(item => {
            return item.tabId && item.tabId === hashtag;
        });

        if (anchorLinkExists) {
            this.anchorLink = hashtag;
            this.activeItem();
            setTimeout(() => {
                const yOffset = getHeaderHeight().scrolledHeight + 30;
                const y = this.host.getBoundingClientRect().top + window.scrollY - yOffset;

                window.scrollTo({ top: y, behavior: 'smooth' });
            }, 300);
        }
    };

    private updateTabIndicator = (): void => {
        const activeTab = this.tabNavRef.querySelector('.tab.--active') as HTMLElement;

        if (activeTab) {
            const indicator = this.tabIndicatorRef;

            indicator.style.width = `${activeTab.offsetWidth}px`;
            indicator.style.transform = `translateX(${activeTab.offsetLeft}px)`;
        }
    };

    render() {
        return (
            <Host>
                <div class="main-content-container">
                    <div class="tabs-wrapper">
                        {this.headline && <h2 innerHTML={this.headline}></h2>}
                        <div class="tabs-nav-container" ref={el => (this.scrollAreaElementRef = el)}>
                            <button
                                class={{
                                    'scroll-btn': true,
                                    'scroll-btn--left': true,
                                    '--hide': !this.isScrollableToLeft,
                                }}
                                onClick={this.scrollToLeft}
                                aria-label={this.scrollLeftAriaLabel}
                            >
                                <sew-icon class="svg" icon="arrow-chevron-left" height="28" width="28" />
                            </button>

                            <div
                                onScroll={this.checkScrollable}
                                ref={ref => (this.tabNavRef = ref)}
                                class="tabs-nav"
                            >
                                {this.hostChildren?.map((tab: HTMLSewWsTabsItemElement, index) => {
                                    return (
                                        <div
                                            class={{
                                                'tab': true,
                                                '--active': this.isActiveTab(tab, index),
                                            }}
                                            onClick={event => this.activeTabHandler(index, event)}
                                            innerHTML={tab.name}
                                            data-sew-trackme-event-category={tab.getAttribute(
                                                'data-sew-trackme-event-category'
                                            )}
                                            data-sew-trackme-event-object={tab.getAttribute(
                                                'data-sew-trackme-event-object'
                                            )}
                                            data-sew-trackme-event-action={tab.getAttribute(
                                                'data-sew-trackme-event-action'
                                            )}
                                        ></div>
                                    );
                                })}
                                <div class="tab-indicator" ref={ref => (this.tabIndicatorRef = ref)}></div>
                            </div>

                            <button
                                class={{
                                    'scroll-btn': true,
                                    'scroll-btn--right': true,
                                    '--hide': !this.isScrollableToRight,
                                }}
                                onClick={this.scrollToRight}
                                aria-label={this.scrollRightAriaLabel}
                            >
                                <sew-icon class="svg" icon="arrow-chevron-right" height="28" width="28" />
                            </button>
                        </div>

                        <div class="tab-content" ref={ref => (this.tabContentRef = ref)}>
                            <slot onSlotchange={this.getHostChildren} />
                        </div>

                        <div class="button-wrapper">
                            <slot name="button" />
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
